'use client';
import { Dispatch, ReactNode, useEffect, useMemo, useState } from 'react';
import { SwapState } from '@/domain/swap';
import useSwapData from '@/application/read/swap/useSwapData';
import useAddress from '@/application/read/web3/useAddress';
import useProcessTrade from '@/application/write/swap/useProcessTrade';
import SwapInput from './SwapInput';
import { useModalContext } from '@/presentation/context/modal';
import { useNetwork } from '@/presentation/context/network';
import Swap from './Swap';
import SwapSummary from './SwapSummary';
import SwapProcessing from './states/SwapProcessing';
import SwapReady from './states/SwapReady';
import SwapError from './states/SwapError';
import SwapSuccess from './states/SwapSuccess';
import SwapSearch from './SwapSearch';
import { Status } from './types';
import { useAccount } from 'wagmi';
import { parseEther } from 'viem';
import { SwapAction } from '@/domain/swap/swapStateReducer';
import Settings from '../Settings';
import type { Address } from 'nftx.js';

export type SwapContainerProps = SwapState & {
  title?: ReactNode;
  dispatch: Dispatch<SwapAction>;
  className?: string;
  inContainer?: boolean;
  withinModal?: boolean;
  canChangeBuyToken?: boolean;
  canChangeSellToken?: boolean;
  showBuy?: boolean;
  showSell?: boolean;
  canFlip?: boolean;
  searchView?: 'wallet' | 'vault';
  onComplete?: () => void;
  onClose?: () => void;
};

function SwapContainer({
  className,
  inContainer,
  withinModal,
  canChangeBuyToken = true,
  canChangeSellToken = true,
  showBuy = true,
  showSell = true,
  canFlip: canFlipOverride = true,
  dispatch,
  title,
  searchView: initialSearchView,
  onComplete,
  onClose,
  ...state
}: SwapContainerProps) {
  const [searchView, setSearchView] = useState<'wallet' | 'vault' | undefined>(
    initialSearchView
  );
  const { network, chain } = useNetwork();
  const { isConnected } = useAccount();
  const userAddress = useAddress();
  const { buyToken, buyType, sellToken, sellType, sellItems, buyItems } = state;
  const data = useSwapData({
    ...state,
    userAddress,
  });
  const {
    buyAmount,
    buyVault,
    sellAmount,
    sellVault,
    buyAmountLoading,
    sellAmountLoading,
    sellBalance,
    buyBalance,
    error,
    sellEthValue,
    buyEthValue,
  } = data;

  const [
    processTrade,
    {
      error: tradeError,
      status: tradeStatus,
      data: tradeData,
      reset: resetTrade,
    },
  ] = useProcessTrade();

  const [status, setStatus] = useState<Status>('ready');

  const stateKey = useMemo(() => {
    return [
      state.buyToken,
      state.buyAmount,
      state.buyItems,
      state.buyType,
      state.sellToken,
      state.sellAmount,
      state.sellItems,
      state.sellType,
    ].join('');
  }, [
    state.buyAmount,
    state.buyItems,
    state.buyToken,
    state.buyType,
    state.sellAmount,
    state.sellItems,
    state.sellToken,
    state.sellType,
  ]);
  useEffect(() => {
    switch (status) {
      case 'ready':
      case 'success':
        break;
      default:
        setStatus('ready');
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateKey]);

  const { showSwapSearchModal, closeModal } = useModalContext();

  const dispatchAndCloseModal: typeof dispatch = (action) => {
    dispatch(action);
    closeModal();
  };

  const dispatchAndHideSearch: typeof dispatch = (action) => {
    dispatch(action);
    setSearchView(undefined);
  };

  const onFlip = () => {
    dispatch({ type: 'flip' });
  };

  // show summary if we have all data for a swap
  // either selling ETH for NFT (and vice versa), or we have buy and sell amounts
  const showSummary = !!data?.quote;

  const canFlip = useMemo(() => {
    if (!canFlipOverride) {
      return false;
    }
    if (sellType === 'NFT' && sellItems.length) {
      return false;
    }
    if (buyType === 'NFT' && buyItems.length) {
      return false;
    }
    return true;
  }, [buyItems.length, buyType, canFlipOverride, sellItems.length, sellType]);

  const hasRequiredBalance = useMemo(() => {
    try {
      if (!sellAmount || !sellBalance) {
        return true;
      }
      if (parseEther(sellBalance) >= parseEther(sellAmount)) {
        return true;
      }
      return false;
    } catch (e) {
      console.error(e);
      return false;
    }
  }, [sellAmount, sellBalance]);

  const handleComplete = () => {
    setStatus('success');
    onComplete?.();
  };
  const handleCancel = () => {
    setStatus('ready');
  };
  const handleClose = () => {
    setStatus('ready');
    resetTrade();
    dispatch({ type: 'reset' });
    onClose?.();
  };

  const finalStatus = tradeStatus === 'Success' ? 'success' : status;

  return (
    <Swap
      inContainer={inContainer}
      className={className}
      status={finalStatus}
      ready={
        <SwapReady
          title={title}
          error={error}
          isConnected={isConnected}
          hasQuote={!!data.quote}
          hasRequiredBalance={hasRequiredBalance}
          canFlip={
            canFlip &&
            (buyType === 'vTOKEN' || buyType === 'ETH' || sellType === 'ETH')
          }
          showBuy={showBuy}
          showSell={showSell}
          onFlip={onFlip}
          onSubmit={() => setStatus('processing')}
          settings={<Settings />}
          buy={
            showBuy && (
              <SwapInput
                source="vault"
                network={network}
                fetching={buyAmountLoading}
                amount={buyAmount}
                token={buyToken}
                dyadType={sellType}
                symbol={buyVault?.token?.symbol ?? ''}
                type={buyType}
                ethAmount={buyEthValue}
                canChangeToken={canChangeBuyToken}
                vaultId={buyVault?.vaultId}
                vault={buyVault}
                amountMax={buyBalance}
                tokenIds={buyItems.map(([tokenId]) => tokenId)}
                onRemoveItem={(tokenId) => {
                  dispatch({
                    type: 'buy_nfts',
                    vaultId: state.buyVaultId,
                    vaultAddress: state.buyVaultAddress as Address,
                    assetAddress: state.buyToken as Address,
                    items: state.buyItems.filter(([id]) => id !== tokenId),
                  });
                }}
                onAmountChange={(amount) => {
                  dispatch({ type: 'change_buy_amount', amount });
                }}
                toggleSearch={() =>
                  withinModal
                    ? setSearchView('vault')
                    : showSwapSearchModal({
                        source: 'vault',
                        ...state,
                        dispatch: dispatchAndCloseModal,
                      })
                }
              />
            )
          }
          sell={
            showSell && (
              <SwapInput
                source="wallet"
                network={network}
                fetching={sellAmountLoading}
                amount={sellAmount}
                token={sellToken}
                dyadType={buyType}
                symbol={sellVault?.token?.symbol ?? ''}
                type={sellType}
                canChangeToken={canChangeSellToken}
                vaultId={sellVault?.vaultId}
                amountMax={sellBalance}
                ethAmount={sellEthValue}
                vault={sellVault}
                tokenIds={sellItems.map(([tokenId]) => tokenId)}
                onRemoveItem={(tokenId) => {
                  dispatch({
                    type: 'sell_nfts',
                    vaultId: state.sellVaultId,
                    vaultAddress: state.sellVaultAddress as Address,
                    assetAddress: state.sellToken as Address,
                    items: state.sellItems.filter(([id]) => id !== tokenId),
                  });
                }}
                onAmountChange={(amount) => {
                  dispatch({ type: 'change_sell_amount', amount });
                }}
                toggleSearch={() =>
                  withinModal
                    ? setSearchView('wallet')
                    : showSwapSearchModal({
                        source: 'wallet',
                        ...state,
                        dispatch: dispatchAndCloseModal,
                      })
                }
              />
            )
          }
          summary={
            showSummary && <SwapSummary {...state} {...data} chain={chain} />
          }
        />
      }
      search={
        withinModal &&
        searchView && (
          <SwapSearch
            {...state}
            source={searchView}
            dispatch={dispatchAndHideSearch}
          />
        )
      }
      error={
        <SwapError
          error={tradeError}
          {...tradeData}
          chain={chain}
          type={data.quote?.type}
          buySymbol={data.buySymbol}
          sellSymbol={data.sellSymbol}
          onRetryClick={() => setStatus('processing')}
          onEditClick={() => setStatus('ready')}
        />
      }
      processing={
        <SwapProcessing
          data={data}
          swapState={state}
          onCancel={handleCancel}
          onComplete={handleComplete}
          processTrade={processTrade}
          error={tradeError}
          status={tradeStatus}
          txHash={tradeData?.transaction?.hash}
          chain={chain}
        />
      }
      success={
        <SwapSuccess
          {...tradeData}
          data={data}
          chain={chain}
          onClose={handleClose}
        />
      }
    />
  );
}

export default SwapContainer;
